import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, ChevronDown, ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import apiService from '../services/nextlevelgames-api';
import { useStore } from '../context/StoreContext';

const sortToApiValue = {
  name: 'name',
  'price-low': 'price_asc',
  'price-high': 'price_desc',
  newest: 'newest',
};

const sortOptions = [
  { value: 'name', label: 'Name: A to Z' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'newest', label: 'Newest First' },
];

const perPageOptions = [25, 50, 100];

const TradingCardGames = () => {
  const [searchParams] = useSearchParams();
  const { locations, selectedLocation, setSelectedLocation } = useStore();

  // Category tree from API
  const [tcgCategories, setTcgCategories] = useState([]);
  const [allTcgIds, setAllTcgIds] = useState([]);

  // Sidebar selection
  const [expandedParentId, setExpandedParentId] = useState(null);
  const [activeCategoryIds, setActiveCategoryIds] = useState([]);

  // Products
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // Controls
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 1024);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [storeOnly, setStoreOnly] = useState(false);

  // Load category tree once
  useEffect(() => {
    apiService.getCategoryTree().then(data => {
      const tcgRoot = (data.categories || []).find(c => c.name === 'Trading Card Games');
      if (!tcgRoot) return;

      const PRIORITY = ['Pokémon Sealed', 'Magic The Gathering Sealed'];
      const pinned = PRIORITY.map(n => tcgRoot.children.find(c => c.name === n)).filter(Boolean);
      const rest = tcgRoot.children.filter(c => !PRIORITY.includes(c.name));
      const sorted = [...pinned, ...rest];
      setTcgCategories(sorted);

      const ids = [];
      sorted.forEach(parent => {
        ids.push(parent.id);
        parent.children.forEach(child => ids.push(child.id));
      });
      setAllTcgIds(ids);
    }).catch(() => {});
  }, []);

  // Handle ?category_id= preselect once tree is loaded
  useEffect(() => {
    if (!tcgCategories.length || !allTcgIds.length) return;
    const preselect = searchParams.get('category_id');
    if (preselect) {
      const parent = tcgCategories.find(p => p.id === preselect);
      if (parent) {
        setExpandedParentId(parent.id);
        setActiveCategoryIds([parent.id, ...parent.children.map(c => c.id)]);
        return;
      }
      for (const parent of tcgCategories) {
        if (parent.children.find(c => c.id === preselect)) {
          setExpandedParentId(parent.id);
          setActiveCategoryIds([preselect]);
          return;
        }
      }
    }
    setActiveCategoryIds(allTcgIds);
  }, [tcgCategories, allTcgIds, searchParams]);

  const fetchItems = useCallback(async (categoryIds, sort, locationIds, limit, offset, onlineOnlyFlag, storeOnlyFlag) => {
    if (!categoryIds.length) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCatalogImages(
        limit, offset, [], sortToApiValue[sort] || 'name', locationIds, [], [], categoryIds, onlineOnlyFlag, storeOnlyFlag
      );
      setCatalogItems((data.items || []).map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        description_html: item.description_html,
        price: item.price_money ? item.price_money.amount / 100 : 0,
        imageUrl: item.image_urls?.[0] || null,
        variation_id: item.variation_id,
        variation_name: item.variation_name,
        location_id: item.location_id,
        present_at_location_ids: item.present_at_location_ids || [],
        quantities_by_location: item.quantities_by_location || {},
        online_available: item.online_available === true,
      })));
      setHasMore(Boolean(data.has_more));
      setTotalResults(data.total ?? 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectedLocationIds = selectedLocation ? [selectedLocation.id] : [];

  // Re-fetch whenever filters change
  useEffect(() => {
    if (!activeCategoryIds.length) return;
    setCurrentPage(0);
    fetchItems(activeCategoryIds, sortBy, selectedLocationIds, itemsPerPage, 0, onlineOnly, storeOnly);
  }, [activeCategoryIds, sortBy, selectedLocation, itemsPerPage, onlineOnly, storeOnly, fetchItems]);

  const handleSelectParent = (parent) => {
    if (expandedParentId === parent.id) {
      setExpandedParentId(null);
      setActiveCategoryIds(allTcgIds);
    } else {
      setExpandedParentId(parent.id);
      setActiveCategoryIds([parent.id, ...parent.children.map(c => c.id)]);
    }
  };

  const handleSelectChild = (e, childId, parentId) => {
    e.stopPropagation();
    setExpandedParentId(parentId);
    setActiveCategoryIds([childId]);
  };

  const handleSelectAll = () => {
    setExpandedParentId(null);
    setActiveCategoryIds(allTcgIds);
  };

  const handleNextPage = () => {
    if (!hasMore) return;
    const next = currentPage + 1;
    setCurrentPage(next);
    fetchItems(activeCategoryIds, sortBy, selectedLocationIds, itemsPerPage, next * itemsPerPage, onlineOnly, storeOnly);
    window.scrollTo(0, 0);
  };

  const handlePreviousPage = () => {
    if (currentPage === 0) return;
    const prev = currentPage - 1;
    setCurrentPage(prev);
    fetchItems(activeCategoryIds, sortBy, selectedLocationIds, itemsPerPage, prev * itemsPerPage, onlineOnly, storeOnly);
    window.scrollTo(0, 0);
  };

  const isAllSelected = expandedParentId === null && activeCategoryIds.length === allTcgIds.length;
  const activeChildId = activeCategoryIds.length === 1 ? activeCategoryIds[0] : null;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Trading Card Games</h1>
          {totalResults > 0 && (
            <p className="text-gray-600">
              {currentPage * itemsPerPage + 1}–{Math.min((currentPage + 1) * itemsPerPage, totalResults)} of {totalResults} results
              {selectedLocation && <span className="ml-1">at {selectedLocation.name}</span>}
            </p>
          )}
        </div>

        {/* Controls Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-primary-100 text-primary-600 border-primary-300' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              <div className="flex items-center gap-2 border-l pl-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:border-primary-500"
                >
                  {sortOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
              </div>

              <div className="hidden md:flex items-center gap-2">
                <span className="text-gray-700">Show:</span>
                <div className="relative">
                  <select
                    value={itemsPerPage}
                    onChange={e => setItemsPerPage(Number(e.target.value))}
                    className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:border-primary-500"
                  >
                    {perPageOptions.map(o => (
                      <option key={o} value={o}>{o} per page</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <AnimatePresence>
            {showFilters && (
              <motion.aside
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="w-64 flex-shrink-0"
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  {/* Store Location — always at top */}
                  {locations.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Store Location</h3>
                      <div className="space-y-2">
                        {locations.map(loc => (
                          <label key={loc.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="store-location"
                              checked={selectedLocation?.id === loc.id}
                              onChange={() => {}}
                              onClick={() => setSelectedLocation(selectedLocation?.id === loc.id ? null : loc)}
                              className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 flex-shrink-0"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-800">{loc.name}</div>
                              <div className="text-xs text-gray-500">{loc.address?.address_line1}, {loc.address?.locality}, {loc.address?.administrative_district_level1}</div>
                            </div>
                          </label>
                        ))}
                        {selectedLocation && (
                          <button
                            onClick={() => setSelectedLocation(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                          >
                            Show all locations
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Availability Filter */}
                  <div className="border-t pt-5 mb-6">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Availability</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={onlineOnly}
                          onChange={e => { setOnlineOnly(e.target.checked); if (e.target.checked) setStoreOnly(false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Available Online</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={storeOnly}
                          onChange={e => { setStoreOnly(e.target.checked); if (e.target.checked) setOnlineOnly(false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">In Store Only</span>
                      </label>
                    </div>
                  </div>

                  {/* Category tree */}
                  <div className={locations.length > 0 ? 'border-t pt-5 mb-6' : 'mb-6'}>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Browse by category</h3>
                    <button
                      onClick={handleSelectAll}
                      className={`block w-full text-left px-2 py-1.5 rounded text-sm mb-1 transition-colors ${isAllSelected ? 'text-primary-600 font-semibold' : 'text-gray-700 hover:text-primary-600'}`}
                    >
                      All Items
                    </button>
                    {tcgCategories.map(parent => {
                      const isExpanded = expandedParentId === parent.id;
                      const isParentActive = isExpanded && (activeCategoryIds.length > 1 || activeCategoryIds[0] === parent.id);
                      return (
                        <div key={parent.id}>
                          <button
                            onClick={() => handleSelectParent(parent)}
                            className={`flex items-center justify-between w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${isParentActive ? 'text-primary-600' : 'text-gray-800 hover:text-primary-600'}`}
                          >
                            <span className={isParentActive ? 'font-bold' : 'font-semibold'}>{parent.name}</span>
                            {parent.children.length > 0 && (
                              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            )}
                          </button>
                          <AnimatePresence>
                            {isExpanded && parent.children.length > 0 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                {parent.children.map(child => (
                                  <button
                                    key={child.id}
                                    onClick={e => handleSelectChild(e, child.id, parent.id)}
                                    className={`block w-full text-left pl-6 pr-2 py-1 rounded text-sm transition-colors ${activeChildId === child.id ? 'text-primary-600 font-medium' : 'text-gray-600 hover:text-primary-600'}`}
                                  >
                                    {child.name}
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => { handleSelectAll(); setSelectedLocation(null); setOnlineOnly(false); setStoreOnly(false); }}
                    className="w-full py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Clear All Filters
                  </button>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Products */}
          <div className="flex-1 min-h-screen">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-lg">{error}</p>
              </div>
            ) : catalogItems.length > 0 ? (
              <>
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'space-y-4'
                }>
                  {catalogItems.map((item, index) => (
                    <motion.div
                      key={item.variation_id || item.id || item.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                    >
                      <ProductCard
                        product={{
                          name: item.name,
                          price: item.price,
                          description: item.description,
                          description_html: item.description_html,
                          variation_id: item.variation_id,
                          variation_name: item.variation_name,
                          location_id: item.location_id,
                          present_at_location_ids: item.present_at_location_ids,
                          quantities_by_location: item.quantities_by_location,
                          selectedLocationIds,
                          online_available: item.online_available,
                        }}
                        imageUrl={item.imageUrl}
                        viewMode={viewMode}
                      />
                    </motion.div>
                  ))}
                </div>

                {(currentPage > 0 || hasMore) && (
                  <div className="flex items-center justify-center gap-3 mt-8">
                    {currentPage > 0 && (
                      <button
                        onClick={handlePreviousPage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Previous Page
                      </button>
                    )}
                    {hasMore && (
                      <button
                        onClick={handleNextPage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        Next Page
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No products found.</p>
                {selectedLocation && (
                  <p className="text-sm text-gray-400 mt-2">
                    No items available at {selectedLocation.name} for this category.{' '}
                    <button onClick={() => setSelectedLocation(null)} className="text-primary-600 hover:underline">
                      Show all locations
                    </button>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingCardGames;
